// src/services/reverseLogisticsService.js
import { pb } from "./pocketbase";
import { correiosService } from "./correiosService";

const N8N_CORREIOS_WEBHOOK_URL = "https://n8n.semprejoias.com.br/webhook/gestao-correios-integracao";

export const reverseLogisticsService = {
  /**
   * Busca todas as solicitações de logística reversa cadastradas no PocketBase.
   */
  async fetchRequests() {
    try {
      return await pb.collection("logistica_reversa").getFullList({
        sort: "-created",
      });
    } catch (error) {
      console.error("Erro ao buscar solicitações de logística reversa:", error);
      throw error;
    }
  },

  /**
   * Solicita um código de autorização de postagem reversa via n8n e salva no PocketBase.
   */
  async createRequest(data) {
    try {
      // 1. Busca a configuração ativa dos Correios para mapear como Destinatário (Siempre Joias)
      const config = await correiosService.fetchConfig();
      if (!config) {
        throw new Error("Configurações dos Correios não encontradas.");
      }

      // Calcula data de validade formatada em DD/MM/YYYY
      const date = new Date();
      date.setDate(date.getDate() + (data.validade_dias || 15));
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = date.getFullYear();
      const dataValidadeLogReversa = `${dd}/${mm}/${yyyy}`;

      const cleanDestCelular = (config.celular || "").replace(/\D/g, "");
      const destCelularFinal = cleanDestCelular.length === 8 ? "9" + cleanDestCelular : cleanDestCelular;

      const cleanRemCelular = (data.customer_phone || "").replace(/\D/g, "");
      const remCelularFinal = cleanRemCelular.length === 8 ? "9" + cleanRemCelular : cleanRemCelular;

      // Inversão de Papéis:
      // Remetente = Cliente final (quem posta o pacote)
      // Destinatário = Siempre Joias (quem recebe o pacote)
      const payload = {
        codigoServico: data.service_code || "03301", // PAC Reverso padrão
        logisticaReversa: "S",
        dataValidadeLogReversa,
        remetente: {
          nome: data.customer_name.trim(),
          cpfCnpj: data.customer_cpf.replace(/\D/g, ""),
          dddCelular: data.customer_phone.substring(0, 2) || "11",
          celular: remCelularFinal.substring(2) || remCelularFinal,
          email: data.customer_email || "cliente@email.com",
          endereco: {
            cep: data.customer_cep.replace(/\D/g, ""),
            logradouro: data.customer_logradouro,
            numero: data.customer_numero || "SN",
            complemento: data.customer_complemento || "",
            bairro: data.customer_bairro,
            cidade: data.customer_cidade,
            uf: data.customer_uf.toUpperCase()
          }
        },
        destinatario: {
          nome: config.remetente_nome,
          cpfCnpj: config.remetente_cnpj,
          dddCelular: config.ddd_celular,
          celular: destCelularFinal,
          email: config.email,
          endereco: {
            cep: config.cep.replace(/\D/g, ""),
            logradouro: config.logradouro,
            numero: config.numero,
            complemento: config.complemento || "",
            bairro: config.bairro,
            cidade: config.cidade,
            uf: config.uf.toUpperCase()
          }
        },
        pesoInformado: "100",
        codigoFormatoObjetoInformado: "2",
        comprimentoInformado: "17",
        larguraInformada: "11", // largura mínima para pacotes
        alturaInformada: "12",
        cienteObjetoNaoProibido: 1,
        itensDeclaracaoConteudo: [
          {
            conteudo: "Garantia Sempre Joias",
            quantidade: "1",
            valor: "50.00"
          }
        ]
      };

      // 2. Envia ao n8n reutilizando o endpoint unificado criarPrePostagem
      const response = await fetch(N8N_CORREIOS_WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "criarPrePostagem",
          payload
        }),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `Erro no n8n: ${response.status}`);
      }

      const result = await response.json();
      if (result.error) {
        throw new Error(result.mensagem || "Erro ao gerar autorização de logística reversa.");
      }

      // Converte o prazo de postagem de retorno dos Correios para AAAA-MM-DD
      const validadeData = result.prazoPostagem ? result.prazoPostagem.split('T')[0] : date.toISOString().split('T')[0];

      // 3. Salva o registro completo no PocketBase
      const dbPayload = {
        order_number: data.order_number,
        customer_name: data.customer_name.trim(),
        customer_cpf: data.customer_cpf.replace(/\D/g, ""),
        codigo_autorizacao: result.codigoObjeto, // E-ticket (AP...)
        codigo_autorizacao_id: result.id, // Correios Prepostagem ID (necessário para revalidar)
        codigo_rastreio: result.codigoObjeto, // Na reversa, o e-ticket e o rastreio são o mesmo código
        status: "GERADO",
        validade: validadeData,
        service_code: data.service_code || "03301",
        customer_cep: data.customer_cep,
        customer_logradouro: data.customer_logradouro,
        customer_numero: data.customer_numero,
        customer_complemento: data.customer_complemento,
        customer_bairro: data.customer_bairro,
        customer_cidade: data.customer_cidade,
        customer_uf: data.customer_uf,
        customer_phone: data.customer_phone,
        customer_email: data.customer_email,
      };

      return await pb.collection("logistica_reversa").create(dbPayload);
    } catch (error) {
      console.error("Erro ao criar solicitação de logística reversa:", error);
      throw error;
    }
  },

  /**
   * Consulta/Sincroniza o status real do e-ticket / rastreamento de retorno no Correios/n8n.
   */
  async syncRequest(request) {
    try {
      if (!request.codigo_autorizacao) {
        throw new Error("Sem código de autorização para sincronizar.");
      }

      const response = await fetch(N8N_CORREIOS_WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "consultarReversa",
          payload: {
            codigoObjeto: request.codigo_autorizacao
          }
        }),
      });

      if (!response.ok) {
        throw new Error("Erro na comunicação com o n8n.");
      }

      const result = await response.json();
      if (result.error) {
        throw new Error(result.mensagem || "Erro ao consultar status nos Correios.");
      }

      // Mapeia os status retornados pela API v2/prepostagens
      let nextStatus = request.status;
      if (result.itens && result.itens.length > 0) {
        const item = result.itens[0];
        const desc = String(item.descStatusAtual || "").toLowerCase();

        if (desc.includes("expirado") || desc.includes("cancelado")) {
          nextStatus = "EXPIRADO";
        } else if (desc.includes("pré-postado") || desc.includes("pre-postado") || desc === "pendente") {
          nextStatus = "GERADO";
        } else if (desc.includes("postado") || desc.includes("preatendido")) {
          nextStatus = "POSTADO";
        } else if (desc.includes("entregue")) {
          nextStatus = "ENTREGUE";
        } else {
          nextStatus = "GERADO";
        }
      }

      const updateData = {
        status: nextStatus
      };

      return await pb.collection("logistica_reversa").update(request.id, updateData);
    } catch (error) {
      console.error("Erro ao sincronizar logística reversa:", error);
      throw error;
    }
  },

  /**
   * Revalida o prazo de postagem da reversa, estendendo-o na API dos Correios e no PocketBase.
   */
  async revalidateRequest(request, validityDays = 15) {
    try {
      if (!request.codigo_autorizacao_id) {
        throw new Error("ID de pré-postagem da autorização não disponível.");
      }

      // Calcula data de validade formatada em DD/MM/YYYY
      const date = new Date();
      date.setDate(date.getDate() + validityDays);
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = date.getFullYear();
      const dataValidadeLogReversa = `${dd}/${mm}/${yyyy}`;

      const response = await fetch(N8N_CORREIOS_WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "revalidarReversa",
          payload: {
            id: request.codigo_autorizacao_id,
            dataValidadeLogReversa
          }
        }),
      });

      if (!response.ok) {
        throw new Error("Erro ao revalidar prazo no n8n.");
      }

      const result = await response.json();
      if (result.error) {
        throw new Error(result.mensagem || "Erro ao revalidar autorização de logística reversa.");
      }

      // Salva no banco de dados a nova validade e retorna o status para GERADO
      const updateData = {
        validade: date.toISOString().split('T')[0],
        status: "GERADO"
      };

      return await pb.collection("logistica_reversa").update(request.id, updateData);
    } catch (error) {
      console.error("Erro ao revalidar logística reversa:", error);
      throw error;
    }
  },

  /**
   * Deleta um registro de logística reversa.
   */
  async deleteRequest(id) {
    try {
      return await pb.collection("logistica_reversa").delete(id);
    } catch (error) {
      console.error("Erro ao deletar logística reversa:", error);
      throw error;
    }
  }
};
